import type { LumberPiecesByProfile } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import type { RoofTruss } from "building-types/framing/roofFramingTypes";
import { RoofFramingType } from "building-types/framing/roofFramingTypes";
import {
  RoofTrussType,
  type RoofTrussBasicInput,
  type TrussMemberSpanData,
} from "building-types/framing/roofTrussTypes";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import { polyLine3New, type PolyLine3 } from "geom/polyLine3";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord,
  generateHipTopChords,
  projectChordsToFramingPlane,
  setupBottomChordRails,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class HipTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      bottomChordProfile,
      bottomChordOffset,
      trussPlane,
      webChordProfile,
    } = input;

    if (!Array.isArray(leftSpan) || !Array.isArray(rightSpan)) {
      throw new Error("Hip truss requires TrussMemberSpanData arrays for spans");
    }

    const leftSpans = leftSpan as TrussMemberSpanData[];
    const rightSpans = rightSpan as TrussMemberSpanData[];

    if (leftSpans.length !== 2 || rightSpans.length !== 2) {
      throw new Error("Hip truss requires exactly two spans on each side");
    }

    // Get maximum top chord height from all profiles used
    const topChordHeight = Math.max(
      ...leftSpans.map((span) => LumberProfileCatalog[span.profile].width),
      ...rightSpans.map((span) => LumberProfileCatalog[span.profile].width),
    );
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const webChordHalfHeight = unsignedNumberNew(LumberProfileCatalog[webChordProfile].width / 2);
    const { left, right } = generateHipTopChords(leftSpans, rightSpans);
    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = this.generateHipBottomChord(
      leftSpans,
      rightSpans,
      leftOverhang,
      rightOverhang,
      unsignedNumberNew(topChordHeight),
      bottomChordHeight,
      bottomChordOffset,
    );

    const allWebChords = this.generateWebChords(
      leftSpans,
      rightSpans,
      bottomChordTopPoints,
      unsignedNumberNew(topChordHeight),
      webChordHalfHeight,
    );

    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      bottomChordTopPointsToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
      ...allWebChordsToFramingPlane
    ] = projectChordsToFramingPlane(
      [left, right, bottomChordTopPoints, leftHeelChord, rightHeelChord, ...allWebChords],
      trussPlane,
    );

    // Create lumber pieces for each profile used in top chords
    const topChords: Record<string, LumberPiecesByProfile> = {};
    leftSpans.forEach((span) => {
      if (!topChords[span.profile]) {
        topChords[span.profile] = lumberPiecesByProfileNew(LumberProfileCatalog[span.profile], { [span.profile]: [] });
      }
    });
    rightSpans.forEach((span) => {
      if (!topChords[span.profile]) {
        topChords[span.profile] = lumberPiecesByProfileNew(LumberProfileCatalog[span.profile], { [span.profile]: [] });
      }
    });

    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });

    const [normX, normY, normZ] = planeNormal(trussPlane);

    // Setup rails for each profile's top chords
    setupTopChordRails(
      topChords[leftSpans[0].profile],
      leftTopChordTopPointsToFramingPlane ? leftTopChordTopPointsToFramingPlane.slice(0, 6) : null,
      leftEaveFasciaSize,
      normX,
      normY,
      normZ,
    );
    setupTopChordRails(
      topChords[leftSpans[1].profile],
      leftTopChordTopPointsToFramingPlane ? leftTopChordTopPointsToFramingPlane.slice(3) : null,
      unsignedNumberNew(1000),
      normX,
      normY,
      normZ,
    );
    setupTopChordRails(
      topChords[rightSpans[0].profile],
      rightTopChordTopPointsToFramingPlane ? rightTopChordTopPointsToFramingPlane.slice(0, 6) : null,
      unsignedNumberNew(1000),
      normX,
      normY,
      normZ,
    );
    setupTopChordRails(
      topChords[rightSpans[1].profile],
      rightTopChordTopPointsToFramingPlane ? rightTopChordTopPointsToFramingPlane.slice(3) : null,
      rightEaveFasciaSize,
      normX,
      normY,
      normZ,
    );

    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, normX, normY, normZ);

    [leftHeelChordToFramingPlane, rightHeelChordToFramingPlane, ...allWebChordsToFramingPlane].forEach((chord) => {
      setupWebChordRails(webChords, chord, normX, normY, normZ);
    });

    return {
      trussType: RoofTrussType.HIP_TRUSS,
      components: [
        ...Object.values(topChords).map((lumber) => ({
          type: RoofFramingComponentType.TOP_CHORD,
          lumber,
        })),
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
  private generateHipBottomChord(
    leftSpans: TrussMemberSpanData[],
    rightSpans: TrussMemberSpanData[],
    leftOverhang: UnsignedNumber,
    rightOverhang: UnsignedNumber,
    topChordHeight: UnsignedNumber,
    bottomChordHeight: UnsignedNumber,
    bottomChordOffset: number,
  ): {
    bottomChord: PolyLine3;
    leftHeelChord: PolyLine3 | null;
    rightHeelChord: PolyLine3 | null;
  } {
    const leftSpan = leftSpans[0].span + leftSpans[1].span;
    const rightSpan = rightSpans[0].span + rightSpans[1].span;
    const leftTopChordPitch = leftSpans[0].pitch;
    const rightTopChordPitch = rightSpans[0].pitch;

    return generateBottomChord(
      unsignedNumberNew(leftSpan),
      unsignedNumberNew(rightSpan),
      leftOverhang,
      rightOverhang,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );
  }
  private generateWebChords(
    leftSpans: TrussMemberSpanData[],
    rightSpans: TrussMemberSpanData[],
    bottomChordTopPoints: PolyLine3,
    topChordHalfHeight: UnsignedNumber,
    webChordHalfHeight: UnsignedNumber,
  ): [PolyLine3, PolyLine3, PolyLine3, PolyLine3, PolyLine3] {
    const leftTopChordPitch = leftSpans[0].pitch;
    const rightTopChordPitch = rightSpans[0].pitch;
    const cosThetaLeft = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
    const sinThetaLeft = leftTopChordPitch * cosThetaLeft;
    const cosThetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
    const sinThetaRight = rightTopChordPitch * cosThetaRight;

    const leftWebChordTopX = -leftSpans[1].span + topChordHalfHeight * sinThetaLeft - webChordHalfHeight;
    const leftWebChordTopZ = -leftSpans[1].span * leftTopChordPitch - topChordHalfHeight;

    const rightWebChordTopX = rightSpans[1].span - topChordHalfHeight * sinThetaRight + webChordHalfHeight;
    const rightWebChordTopZ = -rightSpans[1].span * rightTopChordPitch - topChordHalfHeight;

    const middleWebChordTopX = 0.5 * (leftWebChordTopX + rightWebChordTopX) - webChordHalfHeight;
    const middleWebChordTopZ = 0.5 * (leftWebChordTopZ + rightWebChordTopZ);

    const bottomChordTopZ = 0.5 * (bottomChordTopPoints[2] + bottomChordTopPoints[5]);

    const [leftWebChordBottomX, leftWebChordBottomZ] = [leftWebChordTopX, bottomChordTopZ];
    const [rightWebChordBottomX, rightWebChordBottomZ] = [rightWebChordTopX, bottomChordTopZ];
    const [middleWebChordBottomX, middleWebChordBottomZ] = [middleWebChordTopX, bottomChordTopZ];

    const webChord1 = polyLine3New([
      leftWebChordTopX,
      0,
      leftWebChordTopZ,
      leftWebChordBottomX,
      0,
      leftWebChordBottomZ,
    ]);
    const webChord2 = polyLine3New([
      middleWebChordBottomX,
      0,
      middleWebChordBottomZ,
      middleWebChordTopX,
      0,
      middleWebChordTopZ,
    ]);
    const webChord3 = polyLine3New([
      rightWebChordBottomX,
      0,
      rightWebChordBottomZ,
      rightWebChordTopX,
      0,
      rightWebChordTopZ,
    ]);
    const webChord4 = polyLine3New([
      middleWebChordBottomX,
      0,
      middleWebChordBottomZ,
      leftWebChordTopX,
      0,
      leftWebChordTopZ,
    ]);
    const webChord5 = polyLine3New([
      rightWebChordTopX,
      0,
      rightWebChordTopZ,
      0.5 * (leftWebChordTopX + rightWebChordTopX) + webChordHalfHeight,
      0,
      middleWebChordBottomZ,
    ]);

    return [webChord1, webChord2, webChord3, webChord4, webChord5];
  }
}
